import { motion, AnimatePresence } from "framer-motion";
import React, { useEffect, useState, useRef, useCallback } from "react";

const getDropdownVariants = (direction) => ({
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
      mass: 0.5,
    },
  },
  closed: {
    opacity: 0,
    y: direction === "up" ? 10 : -10,
    scale: 0.96,
    transition: {
      duration: 0.15,
    },
  },
});

const itemVariants = {
  open: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  closed: { opacity: 0, x: -10 },
};

/**
 * Enhanced Dropdown Component with Scroll-to-Load (Infinite Scroll)
 * Modern, beautiful dropdown with pagination support
 */
const EnhancedDropdown = ({
  id,
  title = "Select an option",
  items = [],
  onItemSelect,
  disabled = false,
  isOpen,
  onToggle,
  selectedValue = null,
  error = "",
  // Pagination props for scroll-to-load
  onLoadMore = null,
  hasMore = false,
  loading = false,
  searchable = true,
  placeholder = "Search...",
  label = "", // Add label prop
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find selected item from value
  useEffect(() => {
    if (selectedValue && items.length > 0) {
      const found = items.find(
        (item) =>
          item.id === selectedValue ||
          item.uuid === selectedValue ||
          item.value === selectedValue
      );
      if (found) {
        setSelectedItem(found);
      } else {
        setSelectedItem(null);
      }
    } else {
      setSelectedItem(null);
    }
  }, [selectedValue, items]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    onToggle(id);
    onItemSelect(item.id || item.uuid || item.value);
    setSearchTerm(""); // Clear search on selection
  };

  const filteredItems = items.filter(
    (item) =>
      item?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !onLoadMore || !hasMore || loading) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user scrolls to 80% of the list
    if (scrollPercentage > 0.8) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loading]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer && onLoadMore) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll, onLoadMore]);

  // Calculate dropdown direction based on viewport position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const updateDirection = () => {
        if (!dropdownRef.current) return;
        
        const rect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 320; // Approximate dropdown height with search
        const minSpace = 50; // Minimum space needed
        
        // If not enough space below but enough space above, open upward
        if (spaceBelow < dropdownHeight + minSpace && spaceAbove > spaceBelow) {
          setDropdownDirection("up");
        } else {
          setDropdownDirection("down");
        }
      };
      
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(updateDirection, 10);
      
      // Update on scroll/resize
      window.addEventListener('scroll', updateDirection, true);
      window.addEventListener('resize', updateDirection);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('scroll', updateDirection, true);
        window.removeEventListener('resize', updateDirection);
      };
    } else {
      setDropdownDirection("down"); // Reset to default when closed
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest(`#dropdown-${id}`)) {
        onToggle(id);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, id, onToggle]);

  // Use label as title if provided, otherwise use title prop, fallback to placeholder
  const displayTitle = label || title || placeholder || "Select an option";
  const displayText = selectedItem
    ? selectedItem.title || selectedItem.name || selectedItem.value
    : (placeholder || displayTitle);

  return (
    <motion.div
      ref={dropdownRef}
      id={`dropdown-${id}`}
      className="relative w-full"
      layout
    >
      {/* Dropdown Button */}
      <motion.button
        className={`
          w-full px-3
          ${selectedItem ? "pt-6 pb-2.5" : "py-3.5"}
          text-left text-sm text-gray-900 dark:text-white
          bg-white dark:bg-gray-800
          border-2 rounded-lg
          transition-all duration-200
          focus:outline-none focus:ring-0
          disabled:bg-gray-50 dark:disabled:bg-gray-900
          disabled:cursor-not-allowed
          disabled:opacity-60
          flex items-center justify-between
          placeholder:text-gray-400 dark:placeholder:text-gray-500
          ${
            error
              ? "border-red-500 focus:border-red-600 dark:border-red-500"
              : isOpen
              ? "border-blue-500 focus:border-blue-600 dark:border-blue-500"
              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
          }
        `}
        type="button"
        onClick={() => !disabled && onToggle(id)}
        disabled={disabled}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
      >
        <span className={`truncate flex-1 text-left ${!selectedItem ? "text-gray-400 dark:text-gray-500 italic" : "text-gray-900 dark:text-white"}`}>
          {displayText}
        </span>
        <motion.svg
          className="w-4 h-4 ml-2 flex-shrink-0 text-gray-500 dark:text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </motion.button>

      {/* Focus indicator */}
      {isOpen && !error && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-b-lg"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          exit={{ scaleX: 0 }}
          transition={{ duration: 0.3 }}
        />
      )}

      {/* Error indicator */}
      {error && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-500 rounded-b-lg"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.3 }}
        />
      )}

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`absolute z-[9999] bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full border border-gray-200 dark:border-gray-700 overflow-hidden ${
              dropdownDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"
            }`}
            variants={getDropdownVariants(dropdownDirection)}
            initial="closed"
            animate="open"
            exit="closed"
            layout
            style={{
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              maxHeight: "min(300px, calc(100vh - 120px))",
            }}
          >
            {/* Search Input */}
            {searchable && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-3 border-b border-gray-200 dark:border-gray-700"
              >
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleSearch}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </motion.div>
            )}

            {/* Items List */}
            <div
              ref={scrollContainerRef}
              className="overflow-y-auto dropdown-scrollbar"
              style={{
                maxHeight: dropdownDirection === "up" ? "min(250px, calc(100vh - 120px))" : "min(250px, calc(100vh - 120px))",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e1 #f1f5f9",
              }}
            >
              <motion.ul
                className="py-2"
                initial="closed"
                animate="open"
                variants={{
                  open: {
                    transition: {
                      staggerChildren: 0.02,
                      delayChildren: 0.05,
                    },
                  },
                  closed: { transition: { staggerChildren: 0.01 } },
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => {
                    const isSelected =
                      selectedValue === item.id ||
                      selectedValue === item.uuid ||
                      selectedValue === item.value;

                    return (
                      <motion.li
                        key={item.id || item.uuid || item.value || index}
                        className="px-3"
                        variants={itemVariants}
                      >
                        <motion.div
                          className={`
                            flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer
                            transition-all duration-150
                            ${
                              isSelected
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                                : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                            }
                          `}
                          onClick={() => handleItemClick(item)}
                          whileHover={{ scale: 1.02, x: 2 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <span className="truncate flex-1 capitalize font-medium text-sm">
                            {item.title || item.name || item.value}
                          </span>
                          {isSelected && (
                            <motion.svg
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-4 h-4 ml-2 flex-shrink-0 text-blue-600 dark:text-blue-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </motion.svg>
                          )}
                        </motion.div>
                      </motion.li>
                    );
                  })
                ) : (
                  <motion.li
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-3 py-8 text-center"
                  >
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {searchTerm ? "No results found" : "No options available"}
                    </p>
                  </motion.li>
                )}

                {/* Loading indicator for infinite scroll */}
                {loading && hasMore && (
                  <motion.li
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-3 py-4 text-center"
                  >
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        Loading more...
                      </span>
                    </div>
                  </motion.li>
                )}

                {/* End of list indicator */}
                {!hasMore && filteredItems.length > 5 && (
                  <motion.li
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-3 py-2 text-center"
                  >
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      End of list
                    </p>
                  </motion.li>
                )}
              </motion.ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </motion.p>
      )}
    </motion.div>
  );
};

export default EnhancedDropdown;
